// Blog article translations for i18n support
// This file contains translated content for blog articles

import { blogArticleTranslationsBatch2, slugToTranslationKeyBatch2, translatedArticleSlugsBatch2 } from './blogTranslationsBatch2';
import { blogArticleTranslationsBatch3, slugToTranslationKeyBatch3, translatedArticleSlugsBatch3 } from './blogTranslationsBatch3';
import { blogArticleTranslationsBatch4, slugToTranslationKeyBatch4, translatedArticleSlugsBatch4 } from './blogTranslationsBatch4';
import { blogArticleTranslationsBatch5, slugToTranslationKeyBatch5, translatedArticleSlugsBatch5 } from './blogTranslationsBatch5';
import { blogArticleTranslationsBatch6, slugToTranslationKeyBatch6, translatedArticleSlugsBatch6 } from './blogTranslationsBatch6';
import { blogArticleTranslationsBatch7, slugToTranslationKeyBatch7, translatedArticleSlugsBatch7 } from './blogTranslationsBatch7';

export const blogArticleTranslations = {
  en: {
    // Article 1: go-to-button-quick-routes-accommodation (2025-01-28)
    'blog.article.go-to-button.title': 'New Go-To Button for Quick Routes to the Accommodation',
    'blog.article.go-to-button.excerpt': 'Discover the new Go-To button feature that instantly generates directions to your accommodation, making it easy to share routes via Google Maps, WhatsApp, or email.',
    'blog.article.go-to-button.content': `
## Introducing the Go-To Button

You can now find the brand-new Go-To button in the accommodation window. This practical tool allows you to instantly generate directions from any starting point to the selected accommodation. Whether you start from your home, the airport, the train station or any other address, the Go-To button will create the quickest route for you.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Share Routes Easily

Once generated, the button provides a link that you can easily:

- Open directly in Google Maps for step-by-step navigation
- Share instantly via WhatsApp with family, friends or guests
- Send by email to provide clear travel instructions
- Forward to any smartphone for use on the go

## Why This Matters

This makes giving and sharing directions to accommodations faster, easier and more reliable than typing in the address manually. It saves time, reduces errors and ensures that everyone arrives at the right location without confusion.

## Try It Today

We invite you to try out the new Go-To button and let us know your experience. Your feedback helps us improve and create even smarter tools for our users.
    `,

    // Article 2: flexible-pricing-options-now-available-in-i-rent (2024-12-10)
    'blog.article.flexible-pricing.title': 'Flexible Pricing Options Now Available in i-Rent',
    'blog.article.flexible-pricing.excerpt': 'Manage your rates, set stay durations, and adjust arrival and departure days easier than ever with our new flexible pricing features.',
    'blog.article.flexible-pricing.content': `
## More Flexibility and Convenience

To provide you with even more flexibility and convenience, we've optimized several helpful features in i-Rent. Now, managing your rates, setting stay durations, and adjusting arrival and departure days is easier than ever — all from a single, streamlined view!

## How to Set Up Flexible Pricing

1. Go to your accommodation list and open the information sheet of the relevant property
2. Click on the **Rate Management** tab and select **Change Rate Plan**
3. Choose **Flexible Pricing** and save your changes

![Setting up flexible pricing](/images/blog/flexible-pricing-setup.png)

**Coming soon:** Prices set per length of stay and per person.

## What Can You Do with Flexible Pricing?

- Set minimum and maximum stay durations
- Define flexible rate plans
- Manage arrival and departure days for smoother booking arrangements

In the extended menu of the accommodation (right-click on the accommodation name), go to **Pricing** and select the option you want to adjust from the top menu.

![Flexible pricing menu](/images/blog/flexible-pricing-menu.png)

![Flexible pricing calendar](/images/blog/flexible-pricing-calendar.png)

**Tip:** Need to select multiple days, weeks, or even months at once? Hold down the **Ctrl** key and use your mouse to click on the desired days, weeks or months.

## Experience the Freedom

Experience the freedom and simplicity of these new features, and make your pricing system as flexible as you like!

If you would like a brief introduction, feel free to let us know and we will schedule a Google Meet.
    `,

    // Article 3: new-ota-vacalia-available-in-i-rent (2024-11-15)
    'blog.article.vacalia.title': 'New OTA Available in i-Rent: Vacalia',
    'blog.article.vacalia.excerpt': 'Expand your reach with Vacalia, a new OTA integration now available in i-Rent. Learn how to connect and start publishing your accommodations.',
    'blog.article.vacalia.content': `
## Introducing Vacalia Integration

From now on, you can also publish your accommodations to Vacalia, a growing Online Travel Agency focused on holiday rentals.

## How to Connect

To start publishing your properties on Vacalia:

1. Contact our support team to enable the Vacalia channel
2. Complete the registration process with Vacalia
3. Select which properties you want to publish
4. Your listings will sync automatically

## Benefits of Vacalia

- Access to a new market segment
- Real-time availability synchronization
- Automated pricing updates
- Centralized booking management in i-Rent

## Get Started

Contact us today to add Vacalia to your distribution channels and expand your reach to new guests.
    `,

    // Article 4: guest-reviews-now-available-to-owners (2024-01-17)
    'blog.article.owner-reviews.title': 'Guest Reviews Now Available to Property Owners',
    'blog.article.owner-reviews.excerpt': 'Property owners can now view guest reviews directly in the owner portal, providing greater transparency and actionable insights to improve their accommodations.',
    'blog.article.owner-reviews.content': `
## A New Level of Transparency

We're excited to announce a powerful new feature in the owner portal: property owners can now view guest reviews directly from their dashboard.

This update is designed to improve communication between property managers and owners by providing instant access to valuable guest feedback. Owners can now see exactly what guests appreciate and where there's room for improvement.

## Key Benefits

- **Actionable Insights**: Owners can analyze reviews to understand what guests value most about their property
- **Identify Opportunities**: Spot recurring themes or issues that need attention
- **Better Collaboration**: Facilitate meaningful discussions between managers and owners based on real feedback
- **Track Performance**: Monitor guest satisfaction over time

## How to Enable This Feature

To give an owner access to guest reviews, follow these steps:

1. In i-Rent, navigate to the **People** menu and select **Owners**
2. Click the pencil icon next to the owner's name to open their profile
3. Go to the **Login** tab where you manage permissions
4. Scroll down and enable: **Accommodation → Ratings**
5. Save your changes

![Owner permissions settings](/images/blog/guest-reviews-permissions.png)

## Questions?

For any questions about this feature, please contact us at soporte@i-rent.net.
    `,

    // Article 5: maximize-occupancy-rates-holiday-rentals (2024-01-15)
    'blog.article.maximize-occupancy.title': 'How to Maximize Occupancy Rates for Your Holiday Rentals',
    'blog.article.maximize-occupancy.excerpt': 'Learn proven strategies to keep your properties booked year-round, from dynamic pricing to seasonal promotions.',
    'blog.article.maximize-occupancy.content': `
## Understanding Occupancy Rates

Occupancy rate is one of the most critical metrics for holiday rental success. It measures the percentage of time your property is booked versus available. A healthy occupancy rate typically ranges from 60-80%, depending on your market and property type.

## Strategy 1: Implement Dynamic Pricing

Dynamic pricing adjusts your rates based on demand, seasonality, local events, and competitor pricing. Properties using dynamic pricing see an average 10-20% increase in revenue.

**Tips for dynamic pricing:**
- Use automated pricing tools integrated with your PMS
- Set minimum and maximum rate limits
- Factor in local events and holidays
- Monitor competitor rates regularly

## Strategy 2: Optimize Your Listings

Your listing is your storefront. High-quality photos, compelling descriptions, and complete amenity lists significantly impact booking rates.

**Listing optimization checklist:**
- Professional photography with natural lighting
- Virtual tours or video walkthroughs
- Detailed, benefit-focused descriptions
- Complete and accurate amenity lists
- Quick response to inquiries

## Strategy 3: Multi-Channel Distribution

Don't rely on a single booking platform. Connect to multiple OTAs and maintain your own booking website to maximize visibility.

**Channel strategy:**
- List on major platforms (Booking.com, Airbnb, VRBO)
- Use regional platforms for local markets
- Build your direct booking website
- Use a channel manager for real-time sync

## Strategy 4: Seasonal Promotions

Create targeted promotions for low-demand periods. Last-minute discounts, extended stay offers, and seasonal packages can fill gaps in your calendar.

## Strategy 5: Guest Experience Excellence

Happy guests leave positive reviews, which drive future bookings. Focus on exceeding expectations from first inquiry to checkout.

**Guest experience tips:**
- Fast response times (under 1 hour)
- Personalized welcome messages
- Local recommendations and guidebooks
- Smooth check-in process
- Follow-up after checkout

## Measuring Success

Track your occupancy rate monthly and compare year-over-year. Aim for consistent improvement while maintaining your target ADR (Average Daily Rate).
    `,
  },

  es: {
    // Article 1: go-to-button-quick-routes-accommodation
    'blog.article.go-to-button.title': 'Nuevo Botón Ir-A para Rutas Rápidas al Alojamiento',
    'blog.article.go-to-button.excerpt': 'Descubre el nuevo botón Ir-A que genera direcciones instantáneas a tu alojamiento, facilitando compartir rutas a través de Google Maps, WhatsApp o correo electrónico.',
    'blog.article.go-to-button.content': `
## Presentamos el Botón Ir-A

Ahora puedes encontrar el nuevo botón Ir-A en la ventana del alojamiento. Esta práctica herramienta te permite generar direcciones instantáneamente desde cualquier punto de partida hasta el alojamiento seleccionado. Ya sea que comiences desde tu casa, el aeropuerto, la estación de tren o cualquier otra dirección, el botón Ir-A creará la ruta más rápida para ti.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Comparte Rutas Fácilmente

Una vez generado, el botón proporciona un enlace que puedes fácilmente:

- Abrir directamente en Google Maps para navegación paso a paso
- Compartir instantáneamente por WhatsApp con familiares, amigos o huéspedes
- Enviar por correo electrónico para proporcionar instrucciones claras de viaje
- Reenviar a cualquier smartphone para usar en movimiento

## Por Qué Esto Importa

Esto hace que dar y compartir direcciones a los alojamientos sea más rápido, fácil y confiable que escribir la dirección manualmente. Ahorra tiempo, reduce errores y asegura que todos lleguen al lugar correcto sin confusión.

## Pruébalo Hoy

Te invitamos a probar el nuevo botón Ir-A y contarnos tu experiencia. Tus comentarios nos ayudan a mejorar y crear herramientas aún más inteligentes para nuestros usuarios.
    `,

    // Article 2: flexible-pricing-options-now-available-in-i-rent
    'blog.article.flexible-pricing.title': 'Opciones de Precios Flexibles Ahora Disponibles en i-Rent',
    'blog.article.flexible-pricing.excerpt': 'Gestiona tus tarifas, establece duraciones de estancia y ajusta los días de llegada y salida más fácilmente que nunca con nuestras nuevas funciones de precios flexibles.',
    'blog.article.flexible-pricing.content': `
## Más Flexibilidad y Comodidad

Para brindarte aún más flexibilidad y comodidad, hemos optimizado varias funciones útiles en i-Rent. Ahora, gestionar tus tarifas, establecer duraciones de estancia y ajustar los días de llegada y salida es más fácil que nunca, ¡todo desde una vista única y simplificada!

## Cómo Configurar Precios Flexibles

1. Ve a tu lista de alojamientos y abre la ficha de información de la propiedad relevante
2. Haz clic en la pestaña **Gestión de Tarifas** y selecciona **Cambiar Plan de Tarifas**
3. Elige **Precios Flexibles** y guarda tus cambios

![Configuración de precios flexibles](/images/blog/flexible-pricing-setup.png)

**Próximamente:** Precios establecidos por duración de estancia y por persona.

## ¿Qué Puedes Hacer con Precios Flexibles?

- Establecer duraciones mínimas y máximas de estancia
- Definir planes de tarifas flexibles
- Gestionar días de llegada y salida para reservas más fluidas

En el menú extendido del alojamiento (clic derecho en el nombre del alojamiento), ve a **Precios** y selecciona la opción que deseas ajustar desde el menú superior.

![Menú de precios flexibles](/images/blog/flexible-pricing-menu.png)

![Calendario de precios flexibles](/images/blog/flexible-pricing-calendar.png)

**Consejo:** ¿Necesitas seleccionar varios días, semanas o incluso meses a la vez? Mantén presionada la tecla **Ctrl** y usa el mouse para hacer clic en los días, semanas o meses deseados.

## Experimenta la Libertad

¡Experimenta la libertad y simplicidad de estas nuevas funciones, y haz tu sistema de precios tan flexible como desees!

Si deseas una breve introducción, no dudes en hacérnoslo saber y programaremos una reunión de Google Meet.
    `,

    // Article 3: new-ota-vacalia-available-in-i-rent
    'blog.article.vacalia.title': 'Nueva OTA Disponible en i-Rent: Vacalia',
    'blog.article.vacalia.excerpt': 'Amplía tu alcance con Vacalia, una nueva integración de OTA ahora disponible en i-Rent. Aprende cómo conectar y comenzar a publicar tus alojamientos.',
    'blog.article.vacalia.content': `
## Presentamos la Integración con Vacalia

A partir de ahora, también puedes publicar tus alojamientos en Vacalia, una agencia de viajes online en crecimiento enfocada en alquileres vacacionales.

## Cómo Conectar

Para comenzar a publicar tus propiedades en Vacalia:

1. Contacta a nuestro equipo de soporte para habilitar el canal Vacalia
2. Completa el proceso de registro con Vacalia
3. Selecciona qué propiedades deseas publicar
4. Tus listados se sincronizarán automáticamente

## Beneficios de Vacalia

- Acceso a un nuevo segmento de mercado
- Sincronización de disponibilidad en tiempo real
- Actualizaciones automáticas de precios
- Gestión centralizada de reservas en i-Rent

## Comienza Hoy

Contáctanos hoy para agregar Vacalia a tus canales de distribución y ampliar tu alcance a nuevos huéspedes.
    `,

    // Article 4: guest-reviews-now-available-to-owners
    'blog.article.owner-reviews.title': 'Reseñas de Huéspedes Ahora Disponibles para Propietarios',
    'blog.article.owner-reviews.excerpt': 'Los propietarios ahora pueden ver las reseñas de huéspedes directamente en el portal de propietarios, proporcionando mayor transparencia e información accionable para mejorar sus alojamientos.',
    'blog.article.owner-reviews.content': `
## Un Nuevo Nivel de Transparencia

Nos complace anunciar una nueva función poderosa en el portal de propietarios: los propietarios ahora pueden ver las reseñas de huéspedes directamente desde su panel de control.

Esta actualización está diseñada para mejorar la comunicación entre gestores de propiedades y propietarios al proporcionar acceso instantáneo a valiosos comentarios de huéspedes. Los propietarios ahora pueden ver exactamente qué aprecian los huéspedes y dónde hay espacio para mejorar.

## Beneficios Clave

- **Información Accionable**: Los propietarios pueden analizar las reseñas para entender qué valoran más los huéspedes de su propiedad
- **Identificar Oportunidades**: Detectar temas recurrentes o problemas que necesitan atención
- **Mejor Colaboración**: Facilitar discusiones significativas entre gestores y propietarios basadas en comentarios reales
- **Seguimiento del Rendimiento**: Monitorear la satisfacción de los huéspedes a lo largo del tiempo

## Cómo Habilitar Esta Función

Para dar acceso a un propietario a las reseñas de huéspedes, sigue estos pasos:

1. En i-Rent, navega al menú **Personas** y selecciona **Propietarios**
2. Haz clic en el icono del lápiz junto al nombre del propietario para abrir su perfil
3. Ve a la pestaña **Inicio de Sesión** donde gestionas los permisos
4. Desplázate hacia abajo y habilita: **Alojamiento → Valoraciones**
5. Guarda tus cambios

![Configuración de permisos de propietario](/images/blog/guest-reviews-permissions.png)

## ¿Preguntas?

Para cualquier pregunta sobre esta función, contáctanos en soporte@i-rent.net.
    `,

    // Article 5: maximize-occupancy-rates-holiday-rentals
    'blog.article.maximize-occupancy.title': 'Cómo Maximizar las Tasas de Ocupación de tus Alquileres Vacacionales',
    'blog.article.maximize-occupancy.excerpt': 'Aprende estrategias probadas para mantener tus propiedades reservadas todo el año, desde precios dinámicos hasta promociones estacionales.',
    'blog.article.maximize-occupancy.content': `
## Entendiendo las Tasas de Ocupación

La tasa de ocupación es una de las métricas más críticas para el éxito del alquiler vacacional. Mide el porcentaje de tiempo que tu propiedad está reservada versus disponible. Una tasa de ocupación saludable típicamente oscila entre el 60-80%, dependiendo de tu mercado y tipo de propiedad.

## Estrategia 1: Implementar Precios Dinámicos

Los precios dinámicos ajustan tus tarifas basándose en la demanda, estacionalidad, eventos locales y precios de la competencia. Las propiedades que usan precios dinámicos ven un aumento promedio del 10-20% en ingresos.

**Consejos para precios dinámicos:**
- Usa herramientas de precios automatizadas integradas con tu PMS
- Establece límites mínimos y máximos de tarifas
- Ten en cuenta eventos locales y festivos
- Monitorea las tarifas de la competencia regularmente

## Estrategia 2: Optimiza tus Listados

Tu listado es tu escaparate. Fotos de alta calidad, descripciones convincentes y listas completas de amenidades impactan significativamente las tasas de reserva.

**Lista de verificación para optimización de listados:**
- Fotografía profesional con iluminación natural
- Tours virtuales o recorridos en video
- Descripciones detalladas y enfocadas en beneficios
- Listas completas y precisas de amenidades
- Respuesta rápida a consultas

## Estrategia 3: Distribución Multicanal

No dependas de una sola plataforma de reservas. Conéctate a múltiples OTAs y mantén tu propio sitio web de reservas para maximizar la visibilidad.

**Estrategia de canales:**
- Lista en plataformas principales (Booking.com, Airbnb, VRBO)
- Usa plataformas regionales para mercados locales
- Construye tu sitio web de reservas directas
- Usa un channel manager para sincronización en tiempo real

## Estrategia 4: Promociones Estacionales

Crea promociones dirigidas para períodos de baja demanda. Descuentos de última hora, ofertas de estancia prolongada y paquetes estacionales pueden llenar huecos en tu calendario.

## Estrategia 5: Excelencia en la Experiencia del Huésped

Los huéspedes felices dejan reseñas positivas, que impulsan futuras reservas. Concéntrate en superar expectativas desde la primera consulta hasta el checkout.

**Consejos para la experiencia del huésped:**
- Tiempos de respuesta rápidos (menos de 1 hora)
- Mensajes de bienvenida personalizados
- Recomendaciones locales y guías
- Proceso de check-in fluido
- Seguimiento después del checkout

## Midiendo el Éxito

Rastrea tu tasa de ocupación mensualmente y compara año tras año. Apunta a una mejora consistente mientras mantienes tu ADR objetivo (Tarifa Diaria Promedio).
    `,
  },

  de: {
    // Article 1: go-to-button-quick-routes-accommodation
    'blog.article.go-to-button.title': 'Neuer Go-To-Button für schnelle Routen zur Unterkunft',
    'blog.article.go-to-button.excerpt': 'Entdecken Sie den neuen Go-To-Button, der sofort Wegbeschreibungen zu Ihrer Unterkunft generiert und das Teilen von Routen über Google Maps, WhatsApp oder E-Mail erleichtert.',
    'blog.article.go-to-button.content': `
## Wir präsentieren den Go-To-Button

Sie finden jetzt den brandneuen Go-To-Button im Unterkunftsfenster. Dieses praktische Tool ermöglicht es Ihnen, sofort Wegbeschreibungen von jedem Ausgangspunkt zur ausgewählten Unterkunft zu generieren. Ob Sie von zu Hause, dem Flughafen, dem Bahnhof oder einer anderen Adresse starten, der Go-To-Button erstellt die schnellste Route für Sie.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Routen einfach teilen

Nach der Generierung bietet der Button einen Link, den Sie einfach:

- Direkt in Google Maps für schrittweise Navigation öffnen können
- Sofort über WhatsApp mit Familie, Freunden oder Gästen teilen können
- Per E-Mail senden können, um klare Reiseanweisungen zu geben
- An jedes Smartphone weiterleiten können zur Nutzung unterwegs

## Warum das wichtig ist

Dies macht das Geben und Teilen von Wegbeschreibungen zu Unterkünften schneller, einfacher und zuverlässiger als das manuelle Eintippen der Adresse. Es spart Zeit, reduziert Fehler und stellt sicher, dass alle ohne Verwirrung am richtigen Ort ankommen.

## Probieren Sie es heute aus

Wir laden Sie ein, den neuen Go-To-Button auszuprobieren und uns Ihre Erfahrung mitzuteilen. Ihr Feedback hilft uns, uns zu verbessern und noch intelligentere Tools für unsere Benutzer zu entwickeln.
    `,

    // Article 2: flexible-pricing-options-now-available-in-i-rent
    'blog.article.flexible-pricing.title': 'Flexible Preisoptionen jetzt in i-Rent verfügbar',
    'blog.article.flexible-pricing.excerpt': 'Verwalten Sie Ihre Preise, legen Sie Aufenthaltsdauern fest und passen Sie An- und Abreisetage einfacher denn je mit unseren neuen flexiblen Preisfunktionen an.',
    'blog.article.flexible-pricing.content': `
## Mehr Flexibilität und Komfort

Um Ihnen noch mehr Flexibilität und Komfort zu bieten, haben wir mehrere hilfreiche Funktionen in i-Rent optimiert. Jetzt ist das Verwalten Ihrer Preise, das Festlegen von Aufenthaltsdauern und das Anpassen von An- und Abreisetagen einfacher denn je — alles aus einer einzigen, übersichtlichen Ansicht!

## So richten Sie flexible Preise ein

1. Gehen Sie zu Ihrer Unterkunftsliste und öffnen Sie das Informationsblatt der entsprechenden Immobilie
2. Klicken Sie auf die Registerkarte **Tarifverwaltung** und wählen Sie **Tarifplan ändern**
3. Wählen Sie **Flexible Preise** und speichern Sie Ihre Änderungen

![Einrichtung flexibler Preise](/images/blog/flexible-pricing-setup.png)

**Demnächst:** Preise nach Aufenthaltsdauer und pro Person.

## Was können Sie mit flexiblen Preisen tun?

- Mindest- und Höchstaufenthaltsdauer festlegen
- Flexible Tarifpläne definieren
- An- und Abreisetage für reibungslosere Buchungsabläufe verwalten

Im erweiterten Menü der Unterkunft (Rechtsklick auf den Unterkunftsnamen) gehen Sie zu **Preise** und wählen Sie die Option, die Sie anpassen möchten, aus dem oberen Menü.

![Menü für flexible Preise](/images/blog/flexible-pricing-menu.png)

![Kalender für flexible Preise](/images/blog/flexible-pricing-calendar.png)

**Tipp:** Müssen Sie mehrere Tage, Wochen oder sogar Monate auf einmal auswählen? Halten Sie die **Strg**-Taste gedrückt und klicken Sie mit der Maus auf die gewünschten Tage, Wochen oder Monate.

## Erleben Sie die Freiheit

Erleben Sie die Freiheit und Einfachheit dieser neuen Funktionen und gestalten Sie Ihr Preissystem so flexibel, wie Sie möchten!

Wenn Sie eine kurze Einführung wünschen, teilen Sie uns dies mit und wir vereinbaren ein Google Meet.
    `,

    // Article 3: new-ota-vacalia-available-in-i-rent
    'blog.article.vacalia.title': 'Neue OTA in i-Rent verfügbar: Vacalia',
    'blog.article.vacalia.excerpt': 'Erweitern Sie Ihre Reichweite mit Vacalia, einer neuen OTA-Integration, die jetzt in i-Rent verfügbar ist. Erfahren Sie, wie Sie sich verbinden und Ihre Unterkünfte veröffentlichen.',
    'blog.article.vacalia.content': `
## Wir präsentieren die Vacalia-Integration

Ab sofort können Sie Ihre Unterkünfte auch auf Vacalia veröffentlichen, einer wachsenden Online-Reiseagentur, die sich auf Ferienvermietungen konzentriert.

## So verbinden Sie sich

Um Ihre Immobilien auf Vacalia zu veröffentlichen:

1. Kontaktieren Sie unser Support-Team, um den Vacalia-Kanal zu aktivieren
2. Schließen Sie den Registrierungsprozess bei Vacalia ab
3. Wählen Sie aus, welche Immobilien Sie veröffentlichen möchten
4. Ihre Inserate werden automatisch synchronisiert

## Vorteile von Vacalia

- Zugang zu einem neuen Marktsegment
- Echtzeit-Verfügbarkeitssynchronisierung
- Automatische Preisaktualisierungen
- Zentralisierte Buchungsverwaltung in i-Rent

## Starten Sie jetzt

Kontaktieren Sie uns noch heute, um Vacalia zu Ihren Vertriebskanälen hinzuzufügen und Ihre Reichweite auf neue Gäste zu erweitern.
    `,

    // Article 4: guest-reviews-now-available-to-owners
    'blog.article.owner-reviews.title': 'Gästebewertungen jetzt für Eigentümer verfügbar',
    'blog.article.owner-reviews.excerpt': 'Immobilieneigentümer können jetzt Gästebewertungen direkt im Eigentümerportal einsehen, was mehr Transparenz und umsetzbare Erkenntnisse zur Verbesserung ihrer Unterkünfte bietet.',
    'blog.article.owner-reviews.content': `
## Ein neues Maß an Transparenz

Wir freuen uns, eine leistungsstarke neue Funktion im Eigentümerportal ankündigen zu können: Immobilieneigentümer können jetzt Gästebewertungen direkt von ihrem Dashboard aus einsehen.

Dieses Update wurde entwickelt, um die Kommunikation zwischen Immobilienverwaltern und Eigentümern zu verbessern, indem sofortiger Zugang zu wertvollem Gäste-Feedback ermöglicht wird. Eigentümer können jetzt genau sehen, was Gäste schätzen und wo Verbesserungspotenzial besteht.

## Wichtige Vorteile

- **Umsetzbare Erkenntnisse**: Eigentümer können Bewertungen analysieren, um zu verstehen, was Gäste an ihrer Immobilie am meisten schätzen
- **Chancen erkennen**: Wiederkehrende Themen oder Probleme identifizieren, die Aufmerksamkeit erfordern
- **Bessere Zusammenarbeit**: Sinnvolle Diskussionen zwischen Verwaltern und Eigentümern basierend auf echtem Feedback ermöglichen
- **Leistung verfolgen**: Gästezufriedenheit über die Zeit überwachen

## So aktivieren Sie diese Funktion

Um einem Eigentümer Zugang zu Gästebewertungen zu geben, folgen Sie diesen Schritten:

1. Navigieren Sie in i-Rent zum Menü **Personen** und wählen Sie **Eigentümer**
2. Klicken Sie auf das Stiftsymbol neben dem Namen des Eigentümers, um sein Profil zu öffnen
3. Gehen Sie zur Registerkarte **Anmeldung**, wo Sie Berechtigungen verwalten
4. Scrollen Sie nach unten und aktivieren Sie: **Unterkunft → Bewertungen**
5. Speichern Sie Ihre Änderungen

![Eigentümer-Berechtigungseinstellungen](/images/blog/guest-reviews-permissions.png)

## Fragen?

Bei Fragen zu dieser Funktion kontaktieren Sie uns unter support@i-rent.net.
    `,

    // Article 5: maximize-occupancy-rates-holiday-rentals
    'blog.article.maximize-occupancy.title': 'So maximieren Sie die Belegungsraten Ihrer Ferienvermietungen',
    'blog.article.maximize-occupancy.excerpt': 'Lernen Sie bewährte Strategien, um Ihre Immobilien ganzjährig gebucht zu halten, von dynamischer Preisgestaltung bis zu saisonalen Werbeaktionen.',
    'blog.article.maximize-occupancy.content': `
## Belegungsraten verstehen

Die Belegungsrate ist eine der wichtigsten Kennzahlen für den Erfolg bei Ferienvermietungen. Sie misst den Prozentsatz der Zeit, in der Ihre Immobilie gebucht im Vergleich zu verfügbar ist. Eine gesunde Belegungsrate liegt typischerweise zwischen 60-80%, je nach Ihrem Markt und Immobilientyp.

## Strategie 1: Dynamische Preisgestaltung implementieren

Dynamische Preisgestaltung passt Ihre Preise basierend auf Nachfrage, Saisonalität, lokalen Veranstaltungen und Wettbewerberpreisen an. Immobilien mit dynamischer Preisgestaltung verzeichnen eine durchschnittliche Umsatzsteigerung von 10-20%.

**Tipps für dynamische Preisgestaltung:**
- Verwenden Sie automatisierte Preistools, die in Ihr PMS integriert sind
- Legen Sie Mindest- und Höchstpreisgrenzen fest
- Berücksichtigen Sie lokale Veranstaltungen und Feiertage
- Überwachen Sie regelmäßig die Preise der Wettbewerber

## Strategie 2: Optimieren Sie Ihre Inserate

Ihr Inserat ist Ihr Schaufenster. Hochwertige Fotos, überzeugende Beschreibungen und vollständige Ausstattungslisten beeinflussen die Buchungsraten erheblich.

**Checkliste zur Inseratoptimierung:**
- Professionelle Fotografie mit natürlichem Licht
- Virtuelle Rundgänge oder Video-Walkthroughs
- Detaillierte, nutzenorientierte Beschreibungen
- Vollständige und genaue Ausstattungslisten
- Schnelle Antwort auf Anfragen

## Strategie 3: Multi-Channel-Vertrieb

Verlassen Sie sich nicht auf eine einzige Buchungsplattform. Verbinden Sie sich mit mehreren OTAs und pflegen Sie Ihre eigene Buchungswebsite, um die Sichtbarkeit zu maximieren.

**Kanalstrategie:**
- Listen Sie auf großen Plattformen (Booking.com, Airbnb, VRBO)
- Nutzen Sie regionale Plattformen für lokale Märkte
- Erstellen Sie Ihre Direktbuchungs-Website
- Verwenden Sie einen Channel-Manager für Echtzeit-Synchronisierung

## Strategie 4: Saisonale Werbeaktionen

Erstellen Sie gezielte Werbeaktionen für Zeiten mit geringer Nachfrage. Last-Minute-Rabatte, Angebote für längere Aufenthalte und saisonale Pakete können Lücken in Ihrem Kalender füllen.

## Strategie 5: Exzellenz im Gästeerlebnis

Zufriedene Gäste hinterlassen positive Bewertungen, die zukünftige Buchungen vorantreiben. Konzentrieren Sie sich darauf, Erwartungen von der ersten Anfrage bis zum Checkout zu übertreffen.

**Tipps für das Gästeerlebnis:**
- Schnelle Antwortzeiten (unter 1 Stunde)
- Personalisierte Willkommensnachrichten
- Lokale Empfehlungen und Reiseführer
- Reibungsloser Check-in-Prozess
- Nachverfolgung nach dem Checkout

## Erfolg messen

Verfolgen Sie Ihre Belegungsrate monatlich und vergleichen Sie sie von Jahr zu Jahr. Streben Sie nach kontinuierlicher Verbesserung bei gleichzeitiger Aufrechterhaltung Ihres Ziel-ADR (durchschnittliche Tagesrate).
    `,
  },

  fr: {
    // Article 1: go-to-button-quick-routes-accommodation
    'blog.article.go-to-button.title': 'Nouveau Bouton Aller-À pour des Itinéraires Rapides vers l\'Hébergement',
    'blog.article.go-to-button.excerpt': 'Découvrez le nouveau bouton Aller-À qui génère instantanément des itinéraires vers votre hébergement, facilitant le partage de routes via Google Maps, WhatsApp ou e-mail.',
    'blog.article.go-to-button.content': `
## Présentation du Bouton Aller-À

Vous pouvez maintenant trouver le tout nouveau bouton Aller-À dans la fenêtre d'hébergement. Cet outil pratique vous permet de générer instantanément des itinéraires depuis n'importe quel point de départ jusqu'à l'hébergement sélectionné. Que vous partiez de chez vous, de l'aéroport, de la gare ou de toute autre adresse, le bouton Aller-À créera l'itinéraire le plus rapide pour vous.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Partagez les Itinéraires Facilement

Une fois généré, le bouton fournit un lien que vous pouvez facilement :

- Ouvrir directement dans Google Maps pour une navigation étape par étape
- Partager instantanément via WhatsApp avec la famille, les amis ou les invités
- Envoyer par e-mail pour fournir des instructions de voyage claires
- Transférer vers n'importe quel smartphone pour une utilisation en déplacement

## Pourquoi C'est Important

Cela rend le partage d'itinéraires vers les hébergements plus rapide, plus facile et plus fiable que de saisir l'adresse manuellement. Cela fait gagner du temps, réduit les erreurs et garantit que tout le monde arrive au bon endroit sans confusion.

## Essayez-le Aujourd'hui

Nous vous invitons à essayer le nouveau bouton Aller-À et à nous faire part de votre expérience. Vos commentaires nous aident à nous améliorer et à créer des outils encore plus intelligents pour nos utilisateurs.
    `,

    // Article 2: flexible-pricing-options-now-available-in-i-rent
    'blog.article.flexible-pricing.title': 'Options de Tarification Flexible Maintenant Disponibles dans i-Rent',
    'blog.article.flexible-pricing.excerpt': 'Gérez vos tarifs, définissez les durées de séjour et ajustez les jours d\'arrivée et de départ plus facilement que jamais avec nos nouvelles fonctionnalités de tarification flexible.',
    'blog.article.flexible-pricing.content': `
## Plus de Flexibilité et de Commodité

Pour vous offrir encore plus de flexibilité et de commodité, nous avons optimisé plusieurs fonctionnalités utiles dans i-Rent. Maintenant, gérer vos tarifs, définir les durées de séjour et ajuster les jours d'arrivée et de départ est plus facile que jamais — le tout depuis une vue unique et simplifiée !

## Comment Configurer la Tarification Flexible

1. Allez à votre liste d'hébergements et ouvrez la fiche d'information de la propriété concernée
2. Cliquez sur l'onglet **Gestion des Tarifs** et sélectionnez **Modifier le Plan Tarifaire**
3. Choisissez **Tarification Flexible** et enregistrez vos modifications

![Configuration de la tarification flexible](/images/blog/flexible-pricing-setup.png)

**Bientôt disponible :** Prix par durée de séjour et par personne.

## Que Pouvez-Vous Faire avec la Tarification Flexible ?

- Définir des durées de séjour minimales et maximales
- Définir des plans tarifaires flexibles
- Gérer les jours d'arrivée et de départ pour des réservations plus fluides

Dans le menu étendu de l'hébergement (clic droit sur le nom de l'hébergement), allez à **Tarification** et sélectionnez l'option que vous souhaitez ajuster dans le menu supérieur.

![Menu de tarification flexible](/images/blog/flexible-pricing-menu.png)

![Calendrier de tarification flexible](/images/blog/flexible-pricing-calendar.png)

**Conseil :** Besoin de sélectionner plusieurs jours, semaines ou même mois à la fois ? Maintenez la touche **Ctrl** enfoncée et cliquez sur les jours, semaines ou mois souhaités.

## Vivez la Liberté

Vivez la liberté et la simplicité de ces nouvelles fonctionnalités, et rendez votre système de tarification aussi flexible que vous le souhaitez !

Si vous souhaitez une brève introduction, n'hésitez pas à nous le faire savoir et nous organiserons un Google Meet.
    `,

    // Article 3: new-ota-vacalia-available-in-i-rent
    'blog.article.vacalia.title': 'Nouvelle OTA Disponible dans i-Rent : Vacalia',
    'blog.article.vacalia.excerpt': 'Élargissez votre portée avec Vacalia, une nouvelle intégration OTA maintenant disponible dans i-Rent. Apprenez comment vous connecter et commencer à publier vos hébergements.',
    'blog.article.vacalia.content': `
## Présentation de l'Intégration Vacalia

Désormais, vous pouvez également publier vos hébergements sur Vacalia, une agence de voyages en ligne en pleine croissance spécialisée dans les locations de vacances.

## Comment Se Connecter

Pour commencer à publier vos propriétés sur Vacalia :

1. Contactez notre équipe de support pour activer le canal Vacalia
2. Complétez le processus d'inscription avec Vacalia
3. Sélectionnez les propriétés que vous souhaitez publier
4. Vos annonces se synchroniseront automatiquement

## Avantages de Vacalia

- Accès à un nouveau segment de marché
- Synchronisation des disponibilités en temps réel
- Mises à jour automatiques des prix
- Gestion centralisée des réservations dans i-Rent

## Commencez Maintenant

Contactez-nous aujourd'hui pour ajouter Vacalia à vos canaux de distribution et élargir votre portée auprès de nouveaux clients.
    `,

    // Article 4: guest-reviews-now-available-to-owners
    'blog.article.owner-reviews.title': 'Avis des Clients Maintenant Disponibles pour les Propriétaires',
    'blog.article.owner-reviews.excerpt': 'Les propriétaires peuvent maintenant consulter les avis des clients directement dans le portail propriétaire, offrant plus de transparence et des informations exploitables pour améliorer leurs hébergements.',
    'blog.article.owner-reviews.content': `
## Un Nouveau Niveau de Transparence

Nous sommes ravis d'annoncer une nouvelle fonctionnalité puissante dans le portail propriétaire : les propriétaires peuvent maintenant consulter les avis des clients directement depuis leur tableau de bord.

Cette mise à jour est conçue pour améliorer la communication entre les gestionnaires de propriétés et les propriétaires en fournissant un accès instantané aux précieux commentaires des clients. Les propriétaires peuvent maintenant voir exactement ce que les clients apprécient et où il y a place à l'amélioration.

## Avantages Clés

- **Informations Exploitables** : Les propriétaires peuvent analyser les avis pour comprendre ce que les clients apprécient le plus dans leur propriété
- **Identifier les Opportunités** : Repérer les thèmes récurrents ou les problèmes qui nécessitent attention
- **Meilleure Collaboration** : Faciliter des discussions constructives entre gestionnaires et propriétaires basées sur des commentaires réels
- **Suivre la Performance** : Surveiller la satisfaction des clients au fil du temps

## Comment Activer Cette Fonctionnalité

Pour donner accès aux avis des clients à un propriétaire, suivez ces étapes :

1. Dans i-Rent, naviguez vers le menu **Personnes** et sélectionnez **Propriétaires**
2. Cliquez sur l'icône du crayon à côté du nom du propriétaire pour ouvrir son profil
3. Allez à l'onglet **Connexion** où vous gérez les permissions
4. Faites défiler vers le bas et activez : **Hébergement → Évaluations**
5. Enregistrez vos modifications

![Paramètres des permissions propriétaire](/images/blog/guest-reviews-permissions.png)

## Questions ?

Pour toute question concernant cette fonctionnalité, contactez-nous à support@i-rent.net.
    `,

    // Article 5: maximize-occupancy-rates-holiday-rentals
    'blog.article.maximize-occupancy.title': 'Comment Maximiser les Taux d\'Occupation de Vos Locations de Vacances',
    'blog.article.maximize-occupancy.excerpt': 'Apprenez des stratégies éprouvées pour garder vos propriétés réservées toute l\'année, de la tarification dynamique aux promotions saisonnières.',
    'blog.article.maximize-occupancy.content': `
## Comprendre les Taux d'Occupation

Le taux d'occupation est l'une des métriques les plus critiques pour le succès de la location de vacances. Il mesure le pourcentage de temps où votre propriété est réservée par rapport à disponible. Un taux d'occupation sain varie généralement entre 60-80%, selon votre marché et type de propriété.

## Stratégie 1 : Implémenter la Tarification Dynamique

La tarification dynamique ajuste vos tarifs en fonction de la demande, de la saisonnalité, des événements locaux et des prix des concurrents. Les propriétés utilisant la tarification dynamique voient une augmentation moyenne de 10-20% des revenus.

**Conseils pour la tarification dynamique :**
- Utilisez des outils de tarification automatisés intégrés à votre PMS
- Définissez des limites de tarifs minimales et maximales
- Tenez compte des événements locaux et des jours fériés
- Surveillez régulièrement les tarifs des concurrents

## Stratégie 2 : Optimisez Vos Annonces

Votre annonce est votre vitrine. Des photos de haute qualité, des descriptions convaincantes et des listes d'équipements complètes impactent significativement les taux de réservation.

**Liste de vérification pour l'optimisation des annonces :**
- Photographie professionnelle avec éclairage naturel
- Visites virtuelles ou vidéos de présentation
- Descriptions détaillées axées sur les avantages
- Listes d'équipements complètes et précises
- Réponse rapide aux demandes

## Stratégie 3 : Distribution Multi-Canal

Ne dépendez pas d'une seule plateforme de réservation. Connectez-vous à plusieurs OTAs et maintenez votre propre site de réservation pour maximiser la visibilité.

**Stratégie de canaux :**
- Listez sur les principales plateformes (Booking.com, Airbnb, VRBO)
- Utilisez des plateformes régionales pour les marchés locaux
- Créez votre site de réservation directe
- Utilisez un channel manager pour la synchronisation en temps réel

## Stratégie 4 : Promotions Saisonnières

Créez des promotions ciblées pour les périodes de faible demande. Les réductions de dernière minute, les offres de séjour prolongé et les forfaits saisonniers peuvent combler les lacunes de votre calendrier.

## Stratégie 5 : Excellence de l'Expérience Client

Les clients satisfaits laissent des avis positifs, qui génèrent de futures réservations. Concentrez-vous sur le dépassement des attentes de la première demande au départ.

**Conseils pour l'expérience client :**
- Temps de réponse rapides (moins d'1 heure)
- Messages de bienvenue personnalisés
- Recommandations locales et guides
- Processus de check-in fluide
- Suivi après le départ

## Mesurer le Succès

Suivez votre taux d'occupation mensuellement et comparez d'année en année. Visez une amélioration constante tout en maintenant votre ADR cible (Tarif Journalier Moyen).
    `,
  },

  nl: {
    // Article 1: go-to-button-quick-routes-accommodation
    'blog.article.go-to-button.title': 'Nieuwe Ga-Naar Knop voor Snelle Routes naar de Accommodatie',
    'blog.article.go-to-button.excerpt': 'Ontdek de nieuwe Ga-Naar knop die direct routebeschrijvingen naar uw accommodatie genereert, waardoor het eenvoudig is om routes te delen via Google Maps, WhatsApp of e-mail.',
    'blog.article.go-to-button.content': `
## Introductie van de Ga-Naar Knop

U vindt nu de gloednieuwe Ga-Naar knop in het accommodatievenster. Dit praktische hulpmiddel stelt u in staat om direct routebeschrijvingen te genereren vanaf elk startpunt naar de geselecteerde accommodatie. Of u nu start vanaf thuis, de luchthaven, het treinstation of een ander adres, de Ga-Naar knop maakt de snelste route voor u aan.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Deel Routes Eenvoudig

Na het genereren biedt de knop een link die u eenvoudig kunt:

- Direct openen in Google Maps voor stap-voor-stap navigatie
- Direct delen via WhatsApp met familie, vrienden of gasten
- Per e-mail versturen voor duidelijke reisinstructies
- Doorsturen naar elke smartphone voor onderweg gebruik

## Waarom Dit Belangrijk Is

Dit maakt het geven en delen van routebeschrijvingen naar accommodaties sneller, gemakkelijker en betrouwbaarder dan het handmatig intypen van het adres. Het bespaart tijd, vermindert fouten en zorgt ervoor dat iedereen zonder verwarring op de juiste locatie aankomt.

## Probeer Het Vandaag

Wij nodigen u uit om de nieuwe Ga-Naar knop uit te proberen en ons uw ervaring te laten weten. Uw feedback helpt ons te verbeteren en nog slimmere tools voor onze gebruikers te creëren.
    `,

    // Article 2: flexible-pricing-options-now-available-in-i-rent
    'blog.article.flexible-pricing.title': 'Flexibele Prijsopties Nu Beschikbaar in i-Rent',
    'blog.article.flexible-pricing.excerpt': 'Beheer uw tarieven, stel verblijfsduren in en pas aankomst- en vertrekdagen eenvoudiger dan ooit aan met onze nieuwe flexibele prijsfuncties.',
    'blog.article.flexible-pricing.content': `
## Meer Flexibiliteit en Gemak

Om u nog meer flexibiliteit en gemak te bieden, hebben we verschillende handige functies in i-Rent geoptimaliseerd. Nu is het beheren van uw tarieven, het instellen van verblijfsduren en het aanpassen van aankomst- en vertrekdagen eenvoudiger dan ooit — allemaal vanuit één overzichtelijke weergave!

## Hoe Flexibele Prijzen In te Stellen

1. Ga naar uw accommodatielijst en open het informatieblad van de betreffende woning
2. Klik op het tabblad **Tariefbeheer** en selecteer **Tariefplan Wijzigen**
3. Kies **Flexibele Prijzen** en sla uw wijzigingen op

![Instellen van flexibele prijzen](/images/blog/flexible-pricing-setup.png)

**Binnenkort:** Prijzen per verblijfsduur en per persoon.

## Wat Kunt U Doen met Flexibele Prijzen?

- Minimale en maximale verblijfsduur instellen
- Flexibele tariefplannen definiëren
- Aankomst- en vertrekdagen beheren voor soepelere boekingsregelingen

In het uitgebreide menu van de accommodatie (rechtsklik op de accommodatienaam), ga naar **Prijzen** en selecteer de optie die u wilt aanpassen uit het bovenste menu.

![Menu voor flexibele prijzen](/images/blog/flexible-pricing-menu.png)

![Kalender voor flexibele prijzen](/images/blog/flexible-pricing-calendar.png)

**Tip:** Wilt u meerdere dagen, weken of zelfs maanden tegelijk selecteren? Houd de **Ctrl**-toets ingedrukt en klik met uw muis op de gewenste dagen, weken of maanden.

## Ervaar de Vrijheid

Ervaar de vrijheid en eenvoud van deze nieuwe functies, en maak uw prijssysteem zo flexibel als u wilt!

Als u een korte introductie wenst, laat het ons weten en we plannen een Google Meet.
    `,

    // Article 3: new-ota-vacalia-available-in-i-rent
    'blog.article.vacalia.title': 'Nieuwe OTA Beschikbaar in i-Rent: Vacalia',
    'blog.article.vacalia.excerpt': 'Vergroot uw bereik met Vacalia, een nieuwe OTA-integratie die nu beschikbaar is in i-Rent. Leer hoe u verbinding maakt en begint met het publiceren van uw accommodaties.',
    'blog.article.vacalia.content': `
## Introductie van de Vacalia Integratie

Vanaf nu kunt u uw accommodaties ook publiceren op Vacalia, een groeiend online reisbureau gericht op vakantieverhuur.

## Hoe te Verbinden

Om te beginnen met het publiceren van uw woningen op Vacalia:

1. Neem contact op met ons supportteam om het Vacalia-kanaal in te schakelen
2. Voltooi het registratieproces bij Vacalia
3. Selecteer welke woningen u wilt publiceren
4. Uw listings worden automatisch gesynchroniseerd

## Voordelen van Vacalia

- Toegang tot een nieuw marktsegment
- Realtime beschikbaarheidssynchronisatie
- Automatische prijsupdates
- Gecentraliseerd boekingsbeheer in i-Rent

## Begin Vandaag

Neem vandaag nog contact met ons op om Vacalia toe te voegen aan uw distributiekanalen en uw bereik naar nieuwe gasten uit te breiden.
    `,

    // Article 4: guest-reviews-now-available-to-owners
    'blog.article.owner-reviews.title': 'Gastbeoordelingen Nu Beschikbaar voor Eigenaren',
    'blog.article.owner-reviews.excerpt': 'Woningeigenaren kunnen nu gastbeoordelingen direct bekijken in het eigenaarsportaal, wat meer transparantie en bruikbare inzichten biedt om hun accommodaties te verbeteren.',
    'blog.article.owner-reviews.content': `
## Een Nieuw Niveau van Transparantie

We zijn verheugd een krachtige nieuwe functie in het eigenaarsportaal aan te kondigen: woningeigenaren kunnen nu gastbeoordelingen direct vanuit hun dashboard bekijken.

Deze update is ontworpen om de communicatie tussen vastgoedbeheerders en eigenaren te verbeteren door directe toegang te bieden tot waardevolle feedback van gasten. Eigenaren kunnen nu precies zien wat gasten waarderen en waar ruimte is voor verbetering.

## Belangrijke Voordelen

- **Bruikbare Inzichten**: Eigenaren kunnen beoordelingen analyseren om te begrijpen wat gasten het meest waarderen aan hun woning
- **Kansen Identificeren**: Terugkerende thema's of problemen opsporen die aandacht nodig hebben
- **Betere Samenwerking**: Zinvolle discussies tussen beheerders en eigenaren faciliteren op basis van echte feedback
- **Prestaties Volgen**: Gasttevredenheid in de tijd monitoren

## Hoe Deze Functie In te Schakelen

Om een eigenaar toegang te geven tot gastbeoordelingen, volg deze stappen:

1. Navigeer in i-Rent naar het menu **Personen** en selecteer **Eigenaren**
2. Klik op het potloodicoon naast de naam van de eigenaar om hun profiel te openen
3. Ga naar het tabblad **Inloggen** waar u machtigingen beheert
4. Scroll naar beneden en schakel in: **Accommodatie → Beoordelingen**
5. Sla uw wijzigingen op

![Instellingen eigenaarsmachtigingen](/images/blog/guest-reviews-permissions.png)

## Vragen?

Voor vragen over deze functie, neem contact met ons op via support@i-rent.net.
    `,

    // Article 5: maximize-occupancy-rates-holiday-rentals
    'blog.article.maximize-occupancy.title': 'Hoe de Bezettingsgraad van Uw Vakantieverhuur te Maximaliseren',
    'blog.article.maximize-occupancy.excerpt': 'Leer bewezen strategieën om uw woningen het hele jaar door geboekt te houden, van dynamische prijzen tot seizoensgebonden promoties.',
    'blog.article.maximize-occupancy.content': `
## Bezettingsgraden Begrijpen

De bezettingsgraad is een van de meest kritische metrics voor succes bij vakantieverhuur. Het meet het percentage van de tijd dat uw woning geboekt is versus beschikbaar. Een gezonde bezettingsgraad ligt typisch tussen 60-80%, afhankelijk van uw markt en woningtype.

## Strategie 1: Dynamische Prijzen Implementeren

Dynamische prijzen passen uw tarieven aan op basis van vraag, seizoensgebondenheid, lokale evenementen en concurrentieprijzen. Woningen met dynamische prijzen zien een gemiddelde omzetstijging van 10-20%.

**Tips voor dynamische prijzen:**
- Gebruik geautomatiseerde prijstools geïntegreerd met uw PMS
- Stel minimale en maximale tarieflimieten in
- Houd rekening met lokale evenementen en feestdagen
- Monitor regelmatig de tarieven van concurrenten

## Strategie 2: Optimaliseer Uw Listings

Uw listing is uw etalage. Hoogwaardige foto's, overtuigende beschrijvingen en volledige voorzieningen lijsten hebben een significante impact op boekingspercentages.

**Checklist voor listing optimalisatie:**
- Professionele fotografie met natuurlijk licht
- Virtuele rondleidingen of video walkthroughs
- Gedetailleerde, voordeel-gerichte beschrijvingen
- Volledige en accurate voorzieningen lijsten
- Snelle reactie op vragen

## Strategie 3: Multi-Channel Distributie

Vertrouw niet op één boekingsplatform. Verbind met meerdere OTA's en onderhoud uw eigen boekingswebsite om zichtbaarheid te maximaliseren.

**Kanaalstrategie:**
- List op grote platforms (Booking.com, Airbnb, VRBO)
- Gebruik regionale platforms voor lokale markten
- Bouw uw directe boekingswebsite
- Gebruik een channel manager voor realtime synchronisatie

## Strategie 4: Seizoensgebonden Promoties

Creëer gerichte promoties voor periodes met lage vraag. Last-minute kortingen, aanbiedingen voor verlengd verblijf en seizoenspakketten kunnen gaten in uw kalender vullen.

## Strategie 5: Uitmuntende Gastervaring

Tevreden gasten laten positieve beoordelingen achter, die toekomstige boekingen stimuleren. Focus op het overtreffen van verwachtingen van de eerste vraag tot checkout.

**Tips voor gastervaring:**
- Snelle reactietijden (onder 1 uur)
- Gepersonaliseerde welkomstberichten
- Lokale aanbevelingen en gidsen
- Soepel check-in proces
- Follow-up na checkout

## Succes Meten

Volg uw bezettingsgraad maandelijks en vergelijk jaar-op-jaar. Streef naar consistente verbetering terwijl u uw doel-ADR (Gemiddelde Dagprijs) handhaaft.
    `,
  },

  it: {
    // Article 1: go-to-button-quick-routes-accommodation
    'blog.article.go-to-button.title': 'Nuovo Pulsante Vai-A per Percorsi Rapidi verso l\'Alloggio',
    'blog.article.go-to-button.excerpt': 'Scopri il nuovo pulsante Vai-A che genera istantaneamente indicazioni per il tuo alloggio, facilitando la condivisione dei percorsi tramite Google Maps, WhatsApp o e-mail.',
    'blog.article.go-to-button.content': `
## Presentazione del Pulsante Vai-A

Ora puoi trovare il nuovissimo pulsante Vai-A nella finestra dell'alloggio. Questo pratico strumento ti permette di generare istantaneamente indicazioni da qualsiasi punto di partenza all'alloggio selezionato. Che tu parta da casa, dall'aeroporto, dalla stazione ferroviaria o da qualsiasi altro indirizzo, il pulsante Vai-A creerà il percorso più veloce per te.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Condividi i Percorsi Facilmente

Una volta generato, il pulsante fornisce un link che puoi facilmente:

- Aprire direttamente in Google Maps per la navigazione passo-passo
- Condividere istantaneamente via WhatsApp con familiari, amici o ospiti
- Inviare via e-mail per fornire istruzioni di viaggio chiare
- Inoltrare a qualsiasi smartphone per l'uso in movimento

## Perché È Importante

Questo rende la condivisione delle indicazioni verso gli alloggi più veloce, facile e affidabile rispetto alla digitazione manuale dell'indirizzo. Fa risparmiare tempo, riduce gli errori e assicura che tutti arrivino nel posto giusto senza confusione.

## Provalo Oggi

Ti invitiamo a provare il nuovo pulsante Vai-A e a farci sapere la tua esperienza. Il tuo feedback ci aiuta a migliorare e a creare strumenti ancora più intelligenti per i nostri utenti.
    `,

    // Article 2: flexible-pricing-options-now-available-in-i-rent
    'blog.article.flexible-pricing.title': 'Opzioni di Prezzi Flessibili Ora Disponibili in i-Rent',
    'blog.article.flexible-pricing.excerpt': 'Gestisci le tue tariffe, imposta le durate del soggiorno e regola i giorni di arrivo e partenza più facilmente che mai con le nostre nuove funzionalità di prezzi flessibili.',
    'blog.article.flexible-pricing.content': `
## Più Flessibilità e Comodità

Per offrirti ancora più flessibilità e comodità, abbiamo ottimizzato diverse funzionalità utili in i-Rent. Ora, gestire le tue tariffe, impostare le durate del soggiorno e regolare i giorni di arrivo e partenza è più facile che mai — tutto da un'unica vista semplificata!

## Come Configurare i Prezzi Flessibili

1. Vai alla tua lista di alloggi e apri la scheda informativa della proprietà interessata
2. Clicca sulla scheda **Gestione Tariffe** e seleziona **Modifica Piano Tariffario**
3. Scegli **Prezzi Flessibili** e salva le modifiche

![Configurazione prezzi flessibili](/images/blog/flexible-pricing-setup.png)

**Prossimamente:** Prezzi per durata del soggiorno e per persona.

## Cosa Puoi Fare con i Prezzi Flessibili?

- Impostare durate minime e massime del soggiorno
- Definire piani tariffari flessibili
- Gestire i giorni di arrivo e partenza per prenotazioni più fluide

Nel menu esteso dell'alloggio (clic destro sul nome dell'alloggio), vai a **Prezzi** e seleziona l'opzione che desideri regolare dal menu superiore.

![Menu prezzi flessibili](/images/blog/flexible-pricing-menu.png)

![Calendario prezzi flessibili](/images/blog/flexible-pricing-calendar.png)

**Suggerimento:** Devi selezionare più giorni, settimane o anche mesi contemporaneamente? Tieni premuto il tasto **Ctrl** e usa il mouse per cliccare sui giorni, settimane o mesi desiderati.

## Vivi la Libertà

Vivi la libertà e la semplicità di queste nuove funzionalità, e rendi il tuo sistema di prezzi flessibile come desideri!

Se desideri una breve introduzione, faccelo sapere e organizzeremo un Google Meet.
    `,

    // Article 3: new-ota-vacalia-available-in-i-rent
    'blog.article.vacalia.title': 'Nuova OTA Disponibile in i-Rent: Vacalia',
    'blog.article.vacalia.excerpt': 'Espandi la tua portata con Vacalia, una nuova integrazione OTA ora disponibile in i-Rent. Scopri come connetterti e iniziare a pubblicare i tuoi alloggi.',
    'blog.article.vacalia.content': `
## Presentazione dell'Integrazione Vacalia

D'ora in poi, puoi anche pubblicare i tuoi alloggi su Vacalia, un'agenzia di viaggi online in crescita focalizzata sugli affitti vacanza.

## Come Connettersi

Per iniziare a pubblicare le tue proprietà su Vacalia:

1. Contatta il nostro team di supporto per abilitare il canale Vacalia
2. Completa il processo di registrazione con Vacalia
3. Seleziona quali proprietà vuoi pubblicare
4. I tuoi annunci si sincronizzeranno automaticamente

## Vantaggi di Vacalia

- Accesso a un nuovo segmento di mercato
- Sincronizzazione della disponibilità in tempo reale
- Aggiornamenti automatici dei prezzi
- Gestione centralizzata delle prenotazioni in i-Rent

## Inizia Oggi

Contattaci oggi per aggiungere Vacalia ai tuoi canali di distribuzione e ampliare la tua portata verso nuovi ospiti.
    `,

    // Article 4: guest-reviews-now-available-to-owners
    'blog.article.owner-reviews.title': 'Recensioni degli Ospiti Ora Disponibili per i Proprietari',
    'blog.article.owner-reviews.excerpt': 'I proprietari di immobili possono ora visualizzare le recensioni degli ospiti direttamente nel portale proprietari, fornendo maggiore trasparenza e informazioni utili per migliorare i loro alloggi.',
    'blog.article.owner-reviews.content': `
## Un Nuovo Livello di Trasparenza

Siamo lieti di annunciare una potente nuova funzionalità nel portale proprietari: i proprietari di immobili possono ora visualizzare le recensioni degli ospiti direttamente dalla loro dashboard.

Questo aggiornamento è progettato per migliorare la comunicazione tra gestori di proprietà e proprietari fornendo accesso istantaneo ai preziosi feedback degli ospiti. I proprietari possono ora vedere esattamente cosa apprezzano gli ospiti e dove c'è spazio per miglioramenti.

## Vantaggi Chiave

- **Informazioni Utili**: I proprietari possono analizzare le recensioni per capire cosa gli ospiti apprezzano di più della loro proprietà
- **Identificare Opportunità**: Individuare temi ricorrenti o problemi che necessitano attenzione
- **Migliore Collaborazione**: Facilitare discussioni significative tra gestori e proprietari basate su feedback reali
- **Monitorare le Prestazioni**: Monitorare la soddisfazione degli ospiti nel tempo

## Come Abilitare Questa Funzionalità

Per dare a un proprietario accesso alle recensioni degli ospiti, segui questi passaggi:

1. In i-Rent, naviga al menu **Persone** e seleziona **Proprietari**
2. Clicca sull'icona della matita accanto al nome del proprietario per aprire il suo profilo
3. Vai alla scheda **Login** dove gestisci i permessi
4. Scorri verso il basso e abilita: **Alloggio → Valutazioni**
5. Salva le modifiche

![Impostazioni permessi proprietario](/images/blog/guest-reviews-permissions.png)

## Domande?

Per qualsiasi domanda su questa funzionalità, contattaci a support@i-rent.net.
    `,

    // Article 5: maximize-occupancy-rates-holiday-rentals
    'blog.article.maximize-occupancy.title': 'Come Massimizzare i Tassi di Occupazione dei Tuoi Affitti Vacanza',
    'blog.article.maximize-occupancy.excerpt': 'Impara strategie comprovate per mantenere le tue proprietà prenotate tutto l\'anno, dai prezzi dinamici alle promozioni stagionali.',
    'blog.article.maximize-occupancy.content': `
## Capire i Tassi di Occupazione

Il tasso di occupazione è una delle metriche più critiche per il successo degli affitti vacanza. Misura la percentuale di tempo in cui la tua proprietà è prenotata rispetto a disponibile. Un tasso di occupazione sano varia tipicamente tra il 60-80%, a seconda del tuo mercato e tipo di proprietà.

## Strategia 1: Implementare Prezzi Dinamici

I prezzi dinamici regolano le tue tariffe in base alla domanda, alla stagionalità, agli eventi locali e ai prezzi dei concorrenti. Le proprietà che usano prezzi dinamici vedono un aumento medio del 10-20% dei ricavi.

**Consigli per i prezzi dinamici:**
- Usa strumenti di pricing automatizzati integrati con il tuo PMS
- Imposta limiti minimi e massimi delle tariffe
- Considera eventi locali e festività
- Monitora regolarmente le tariffe dei concorrenti

## Strategia 2: Ottimizza i Tuoi Annunci

Il tuo annuncio è la tua vetrina. Foto di alta qualità, descrizioni convincenti e liste complete di servizi impattano significativamente i tassi di prenotazione.

**Checklist per l'ottimizzazione degli annunci:**
- Fotografia professionale con illuminazione naturale
- Tour virtuali o video walkthrough
- Descrizioni dettagliate focalizzate sui benefici
- Liste complete e accurate dei servizi
- Risposta rapida alle richieste

## Strategia 3: Distribuzione Multi-Canale

Non affidarti a una singola piattaforma di prenotazione. Collegati a più OTA e mantieni il tuo sito di prenotazione per massimizzare la visibilità.

**Strategia dei canali:**
- Pubblica sulle principali piattaforme (Booking.com, Airbnb, VRBO)
- Usa piattaforme regionali per i mercati locali
- Costruisci il tuo sito di prenotazione diretta
- Usa un channel manager per la sincronizzazione in tempo reale

## Strategia 4: Promozioni Stagionali

Crea promozioni mirate per i periodi di bassa domanda. Sconti last-minute, offerte per soggiorni prolungati e pacchetti stagionali possono riempire i vuoti nel tuo calendario.

## Strategia 5: Eccellenza nell'Esperienza degli Ospiti

Gli ospiti felici lasciano recensioni positive, che guidano le prenotazioni future. Concentrati sul superare le aspettative dalla prima richiesta al checkout.

**Consigli per l'esperienza degli ospiti:**
- Tempi di risposta rapidi (sotto 1 ora)
- Messaggi di benvenuto personalizzati
- Raccomandazioni locali e guide
- Processo di check-in fluido
- Follow-up dopo il checkout

## Misurare il Successo

Monitora il tuo tasso di occupazione mensilmente e confronta anno su anno. Punta a un miglioramento costante mantenendo il tuo ADR target (Tariffa Giornaliera Media).
    `,
  },

  pt: {
    // Article 1: go-to-button-quick-routes-accommodation
    'blog.article.go-to-button.title': 'Novo Botão Ir-Para para Rotas Rápidas para o Alojamento',
    'blog.article.go-to-button.excerpt': 'Descubra o novo botão Ir-Para que gera instantaneamente direções para o seu alojamento, facilitando a partilha de rotas via Google Maps, WhatsApp ou e-mail.',
    'blog.article.go-to-button.content': `
## Apresentamos o Botão Ir-Para

Agora pode encontrar o novo botão Ir-Para na janela do alojamento. Esta ferramenta prática permite-lhe gerar instantaneamente direções de qualquer ponto de partida até ao alojamento selecionado. Quer comece de casa, do aeroporto, da estação de comboios ou de qualquer outro endereço, o botão Ir-Para criará a rota mais rápida para si.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Partilhe Rotas Facilmente

Uma vez gerado, o botão fornece um link que pode facilmente:

- Abrir diretamente no Google Maps para navegação passo-a-passo
- Partilhar instantaneamente via WhatsApp com família, amigos ou hóspedes
- Enviar por e-mail para fornecer instruções de viagem claras
- Encaminhar para qualquer smartphone para usar em movimento

## Porque É Importante

Isto torna o dar e partilhar direções para alojamentos mais rápido, fácil e fiável do que escrever o endereço manualmente. Poupa tempo, reduz erros e garante que todos chegam ao local certo sem confusão.

## Experimente Hoje

Convidamo-lo a experimentar o novo botão Ir-Para e a dar-nos a sua opinião. O seu feedback ajuda-nos a melhorar e a criar ferramentas ainda mais inteligentes para os nossos utilizadores.
    `,

    // Article 2: flexible-pricing-options-now-available-in-i-rent
    'blog.article.flexible-pricing.title': 'Opções de Preços Flexíveis Agora Disponíveis no i-Rent',
    'blog.article.flexible-pricing.excerpt': 'Gerir as suas tarifas, definir durações de estadia e ajustar os dias de chegada e partida é mais fácil do que nunca com as nossas novas funcionalidades de preços flexíveis.',
    'blog.article.flexible-pricing.content': `
## Mais Flexibilidade e Conveniência

Para lhe oferecer ainda mais flexibilidade e conveniência, optimizámos várias funcionalidades úteis no i-Rent. Agora, gerir as suas tarifas, definir durações de estadia e ajustar os dias de chegada e partida é mais fácil do que nunca — tudo a partir de uma única vista simplificada!

## Como Configurar Preços Flexíveis

1. Vá à sua lista de alojamentos e abra a ficha de informação da propriedade relevante
2. Clique no separador **Gestão de Tarifas** e selecione **Alterar Plano de Tarifas**
3. Escolha **Preços Flexíveis** e guarde as suas alterações

![Configuração de preços flexíveis](/images/blog/flexible-pricing-setup.png)

**Em breve:** Preços por duração de estadia e por pessoa.

## O Que Pode Fazer com Preços Flexíveis?

- Definir durações mínimas e máximas de estadia
- Definir planos tarifários flexíveis
- Gerir dias de chegada e partida para reservas mais fluidas

No menu estendido do alojamento (clique direito no nome do alojamento), vá a **Preços** e selecione a opção que deseja ajustar no menu superior.

![Menu de preços flexíveis](/images/blog/flexible-pricing-menu.png)

![Calendário de preços flexíveis](/images/blog/flexible-pricing-calendar.png)

**Dica:** Precisa de selecionar vários dias, semanas ou mesmo meses de uma vez? Mantenha a tecla **Ctrl** pressionada e use o rato para clicar nos dias, semanas ou meses desejados.

## Experimente a Liberdade

Experimente a liberdade e simplicidade destas novas funcionalidades, e torne o seu sistema de preços tão flexível quanto desejar!

Se desejar uma breve introdução, não hesite em contactar-nos e agendaremos um Google Meet.
    `,

    // Article 3: new-ota-vacalia-available-in-i-rent
    'blog.article.vacalia.title': 'Nova OTA Disponível no i-Rent: Vacalia',
    'blog.article.vacalia.excerpt': 'Expanda o seu alcance com a Vacalia, uma nova integração OTA agora disponível no i-Rent. Aprenda como conectar e começar a publicar os seus alojamentos.',
    'blog.article.vacalia.content': `
## Apresentamos a Integração Vacalia

A partir de agora, também pode publicar os seus alojamentos na Vacalia, uma agência de viagens online em crescimento focada em alugueres de férias.

## Como Conectar

Para começar a publicar as suas propriedades na Vacalia:

1. Contacte a nossa equipa de suporte para ativar o canal Vacalia
2. Complete o processo de registo com a Vacalia
3. Selecione quais propriedades deseja publicar
4. Os seus anúncios serão sincronizados automaticamente

## Benefícios da Vacalia

- Acesso a um novo segmento de mercado
- Sincronização de disponibilidade em tempo real
- Atualizações automáticas de preços
- Gestão centralizada de reservas no i-Rent

## Comece Hoje

Contacte-nos hoje para adicionar a Vacalia aos seus canais de distribuição e expandir o seu alcance a novos hóspedes.
    `,

    // Article 4: guest-reviews-now-available-to-owners
    'blog.article.owner-reviews.title': 'Avaliações de Hóspedes Agora Disponíveis para Proprietários',
    'blog.article.owner-reviews.excerpt': 'Os proprietários de imóveis podem agora ver as avaliações dos hóspedes diretamente no portal de proprietários, proporcionando maior transparência e informações acionáveis para melhorar os seus alojamentos.',
    'blog.article.owner-reviews.content': `
## Um Novo Nível de Transparência

Temos o prazer de anunciar uma nova funcionalidade poderosa no portal de proprietários: os proprietários de imóveis podem agora ver as avaliações dos hóspedes diretamente do seu painel de controlo.

Esta atualização foi concebida para melhorar a comunicação entre gestores de propriedades e proprietários, fornecendo acesso instantâneo a feedback valioso dos hóspedes. Os proprietários podem agora ver exatamente o que os hóspedes apreciam e onde há espaço para melhorias.

## Benefícios Principais

- **Informações Acionáveis**: Os proprietários podem analisar as avaliações para entender o que os hóspedes mais valorizam na sua propriedade
- **Identificar Oportunidades**: Detetar temas recorrentes ou problemas que precisam de atenção
- **Melhor Colaboração**: Facilitar discussões significativas entre gestores e proprietários baseadas em feedback real
- **Acompanhar o Desempenho**: Monitorizar a satisfação dos hóspedes ao longo do tempo

## Como Ativar Esta Funcionalidade

Para dar acesso a um proprietário às avaliações dos hóspedes, siga estes passos:

1. No i-Rent, navegue até ao menu **Pessoas** e selecione **Proprietários**
2. Clique no ícone do lápis junto ao nome do proprietário para abrir o seu perfil
3. Vá ao separador **Login** onde gere as permissões
4. Desloque-se para baixo e ative: **Alojamento → Avaliações**
5. Guarde as suas alterações

![Definições de permissões do proprietário](/images/blog/guest-reviews-permissions.png)

## Questões?

Para qualquer questão sobre esta funcionalidade, contacte-nos em support@i-rent.net.
    `,

    // Article 5: maximize-occupancy-rates-holiday-rentals
    'blog.article.maximize-occupancy.title': 'Como Maximizar as Taxas de Ocupação dos Seus Alugueres de Férias',
    'blog.article.maximize-occupancy.excerpt': 'Aprenda estratégias comprovadas para manter as suas propriedades reservadas durante todo o ano, desde preços dinâmicos a promoções sazonais.',
    'blog.article.maximize-occupancy.content': `
## Compreender as Taxas de Ocupação

A taxa de ocupação é uma das métricas mais críticas para o sucesso do aluguer de férias. Mede a percentagem de tempo em que a sua propriedade está reservada versus disponível. Uma taxa de ocupação saudável varia tipicamente entre 60-80%, dependendo do seu mercado e tipo de propriedade.

## Estratégia 1: Implementar Preços Dinâmicos

Os preços dinâmicos ajustam as suas tarifas com base na procura, sazonalidade, eventos locais e preços dos concorrentes. As propriedades que usam preços dinâmicos veem um aumento médio de 10-20% nas receitas.

**Dicas para preços dinâmicos:**
- Use ferramentas de preços automatizadas integradas com o seu PMS
- Defina limites mínimos e máximos de tarifas
- Considere eventos locais e feriados
- Monitorize regularmente as tarifas dos concorrentes

## Estratégia 2: Otimize os Seus Anúncios

O seu anúncio é a sua montra. Fotos de alta qualidade, descrições convincentes e listas completas de comodidades impactam significativamente as taxas de reserva.

**Lista de verificação para otimização de anúncios:**
- Fotografia profissional com iluminação natural
- Tours virtuais ou vídeos de apresentação
- Descrições detalhadas focadas em benefícios
- Listas completas e precisas de comodidades
- Resposta rápida a pedidos de informação

## Estratégia 3: Distribuição Multi-Canal

Não dependa de uma única plataforma de reservas. Conecte-se a múltiplas OTAs e mantenha o seu próprio site de reservas para maximizar a visibilidade.

**Estratégia de canais:**
- Publique nas principais plataformas (Booking.com, Airbnb, VRBO)
- Use plataformas regionais para mercados locais
- Construa o seu site de reservas diretas
- Use um channel manager para sincronização em tempo real

## Estratégia 4: Promoções Sazonais

Crie promoções direcionadas para períodos de baixa procura. Descontos de última hora, ofertas de estadia prolongada e pacotes sazonais podem preencher lacunas no seu calendário.

## Estratégia 5: Excelência na Experiência do Hóspede

Hóspedes satisfeitos deixam avaliações positivas, que impulsionam reservas futuras. Concentre-se em superar expectativas desde o primeiro contacto até ao checkout.

**Dicas para a experiência do hóspede:**
- Tempos de resposta rápidos (menos de 1 hora)
- Mensagens de boas-vindas personalizadas
- Recomendações locais e guias
- Processo de check-in fluido
- Seguimento após o checkout

## Medir o Sucesso

Acompanhe a sua taxa de ocupação mensalmente e compare ano a ano. Procure uma melhoria consistente mantendo o seu ADR alvo (Tarifa Diária Média).
    `,
  },

  dk: {
    // Article 1: go-to-button-quick-routes-accommodation
    'blog.article.go-to-button.title': 'Ny Gå-Til Knap til Hurtige Ruter til Indkvarteringen',
    'blog.article.go-to-button.excerpt': 'Opdag den nye Gå-Til knap, der øjeblikkeligt genererer vejvisninger til din indkvartering, hvilket gør det nemt at dele ruter via Google Maps, WhatsApp eller e-mail.',
    'blog.article.go-to-button.content': `
## Introduktion af Gå-Til Knappen

Du kan nu finde den helt nye Gå-Til knap i indkvarteringsvinduet. Dette praktiske værktøj gør det muligt for dig øjeblikkeligt at generere vejvisninger fra ethvert udgangspunkt til den valgte indkvartering. Uanset om du starter hjemmefra, fra lufthavnen, togstationen eller en anden adresse, vil Gå-Til knappen oprette den hurtigste rute for dig.

![Go-To Button Route Planner](/images/blog/goto-button-route-planner.png)

## Del Ruter Nemt

Når den er genereret, giver knappen et link, som du nemt kan:

- Åbne direkte i Google Maps til trin-for-trin navigation
- Dele øjeblikkeligt via WhatsApp med familie, venner eller gæster
- Sende via e-mail for at give klare rejseanvisninger
- Videresende til enhver smartphone til brug på farten

## Hvorfor Det Er Vigtigt

Dette gør det hurtigere, nemmere og mere pålideligt at give og dele vejvisninger til indkvarteringer end at indtaste adressen manuelt. Det sparer tid, reducerer fejl og sikrer, at alle ankommer til det rigtige sted uden forvirring.

## Prøv Det I Dag

Vi inviterer dig til at prøve den nye Gå-Til knap og fortælle os om din oplevelse. Din feedback hjælper os med at forbedre og skabe endnu smartere værktøjer til vores brugere.
    `,

    // Article 2: flexible-pricing-options-now-available-in-i-rent
    'blog.article.flexible-pricing.title': 'Fleksible Prisindstillinger Nu Tilgængelige i i-Rent',
    'blog.article.flexible-pricing.excerpt': 'Administrer dine priser, indstil opholdsvarigheder og juster ankomst- og afrejsedage nemmere end nogensinde med vores nye fleksible prisfunktioner.',
    'blog.article.flexible-pricing.content': `
## Mere Fleksibilitet og Bekvemmelighed

For at give dig endnu mere fleksibilitet og bekvemmelighed har vi optimeret flere nyttige funktioner i i-Rent. Nu er det nemmere end nogensinde at administrere dine priser, indstille opholdsvarigheder og justere ankomst- og afrejsedage — alt fra én enkelt, strømlinet visning!

## Sådan Opsætter Du Fleksible Priser

1. Gå til din indkvarteringsliste og åbn informationsarket for den relevante ejendom
2. Klik på fanen **Takststyring** og vælg **Skift Takstplan**
3. Vælg **Fleksible Priser** og gem dine ændringer

![Opsætning af fleksible priser](/images/blog/flexible-pricing-setup.png)

**Kommer snart:** Priser pr. opholdslængde og pr. person.

## Hvad Kan Du Gøre med Fleksible Priser?

- Indstille minimums- og maksimumsopholdsvarigheder
- Definere fleksible takstplaner
- Administrere ankomst- og afrejsedage for smidigere bookingforløb

I det udvidede menu for indkvarteringen (højreklik på indkvarteringsnavnet), gå til **Prissætning** og vælg den mulighed, du vil justere, fra topmenuen.

![Menu for fleksible priser](/images/blog/flexible-pricing-menu.png)

![Kalender for fleksible priser](/images/blog/flexible-pricing-calendar.png)

**Tip:** Skal du vælge flere dage, uger eller endda måneder på én gang? Hold **Ctrl**-tasten nede og brug musen til at klikke på de ønskede dage, uger eller måneder.

## Oplev Friheden

Oplev friheden og enkelheden ved disse nye funktioner, og gør dit prissystem så fleksibelt, som du ønsker!

Hvis du ønsker en kort introduktion, så lad os vide, og vi planlægger et Google Meet.
    `,

    // Article 3: new-ota-vacalia-available-in-i-rent
    'blog.article.vacalia.title': 'Ny OTA Tilgængelig i i-Rent: Vacalia',
    'blog.article.vacalia.excerpt': 'Udvid din rækkevidde med Vacalia, en ny OTA-integration nu tilgængelig i i-Rent. Lær hvordan du forbinder og begynder at udgive dine indkvarteringer.',
    'blog.article.vacalia.content': `
## Introduktion af Vacalia Integration

Fra nu af kan du også udgive dine indkvarteringer på Vacalia, et voksende online rejsebureau fokuseret på ferielejeboliger.

## Sådan Forbinder Du

For at begynde at udgive dine ejendomme på Vacalia:

1. Kontakt vores supportteam for at aktivere Vacalia-kanalen
2. Gennemfør registreringsprocessen med Vacalia
3. Vælg hvilke ejendomme du vil udgive
4. Dine opslag synkroniseres automatisk

## Fordele ved Vacalia

- Adgang til et nyt markedssegment
- Realtids tilgængelighedssynkronisering
- Automatiske prisopdateringer
- Centraliseret bookingstyring i i-Rent

## Kom I Gang

Kontakt os i dag for at tilføje Vacalia til dine distributionskanaler og udvide din rækkevidde til nye gæster.
    `,

    // Article 4: guest-reviews-now-available-to-owners
    'blog.article.owner-reviews.title': 'Gæsteanmeldelser Nu Tilgængelige for Ejere',
    'blog.article.owner-reviews.excerpt': 'Ejendomsejere kan nu se gæsteanmeldelser direkte i ejerportalen, hvilket giver større gennemsigtighed og handlingsorienteret indsigt til at forbedre deres indkvarteringer.',
    'blog.article.owner-reviews.content': `
## Et Nyt Niveau af Gennemsigtighed

Vi er glade for at kunne annoncere en kraftfuld ny funktion i ejerportalen: ejendomsejere kan nu se gæsteanmeldelser direkte fra deres dashboard.

Denne opdatering er designet til at forbedre kommunikationen mellem ejendomsadministratorer og ejere ved at give øjeblikkelig adgang til værdifuld gæstefeedback. Ejere kan nu se præcis, hvad gæster sætter pris på, og hvor der er plads til forbedring.

## Vigtige Fordele

- **Handlingsorienteret Indsigt**: Ejere kan analysere anmeldelser for at forstå, hvad gæster værdsætter mest ved deres ejendom
- **Identificer Muligheder**: Spot tilbagevendende temaer eller problemer, der kræver opmærksomhed
- **Bedre Samarbejde**: Faciliter meningsfulde diskussioner mellem administratorer og ejere baseret på reel feedback
- **Følg Præstationen**: Overvåg gæstetilfredshed over tid

## Sådan Aktiverer Du Denne Funktion

For at give en ejer adgang til gæsteanmeldelser, følg disse trin:

1. I i-Rent, naviger til menuen **Personer** og vælg **Ejere**
2. Klik på blyantsikonet ved siden af ejerens navn for at åbne deres profil
3. Gå til fanen **Login** hvor du administrerer tilladelser
4. Rul ned og aktivér: **Indkvartering → Vurderinger**
5. Gem dine ændringer

![Ejer tilladelsesindstillinger](/images/blog/guest-reviews-permissions.png)

## Spørgsmål?

For spørgsmål om denne funktion, kontakt os på support@i-rent.net.
    `,

    // Article 5: maximize-occupancy-rates-holiday-rentals
    'blog.article.maximize-occupancy.title': 'Sådan Maksimerer Du Belægningsgraden for Dine Ferieudlejninger',
    'blog.article.maximize-occupancy.excerpt': 'Lær gennemprøvede strategier til at holde dine ejendomme booket hele året rundt, fra dynamisk prissætning til sæsonbetonede kampagner.',
    'blog.article.maximize-occupancy.content': `
## Forståelse af Belægningsgrader

Belægningsgraden er en af de mest kritiske målinger for succes med ferieudlejning. Den måler procentdelen af tid, din ejendom er booket versus tilgængelig. En sund belægningsgrad ligger typisk mellem 60-80%, afhængigt af dit marked og ejendomstype.

## Strategi 1: Implementer Dynamisk Prissætning

Dynamisk prissætning justerer dine priser baseret på efterspørgsel, sæsonbetonethed, lokale begivenheder og konkurrentpriser. Ejendomme med dynamisk prissætning ser en gennemsnitlig omsætningsstigning på 10-20%.

**Tips til dynamisk prissætning:**
- Brug automatiserede prisværktøjer integreret med dit PMS
- Sæt minimums- og maksimumsprisgrænser
- Tag højde for lokale begivenheder og helligdage
- Overvåg regelmæssigt konkurrenternes priser

## Strategi 2: Optimer Dine Opslag

Dit opslag er din butiksfacade. Fotos af høj kvalitet, overbevisende beskrivelser og komplette facilitets lister påvirker bookingfrekvensen betydeligt.

**Tjekliste til optimering af opslag:**
- Professionel fotografering med naturligt lys
- Virtuelle ture eller video-gennemgange
- Detaljerede, fordels-fokuserede beskrivelser
- Komplette og præcise facilitetslister
- Hurtig respons på forespørgsler

## Strategi 3: Multi-Kanal Distribution

Stol ikke på én enkelt bookingplatform. Forbind til flere OTA'er og vedligehold din egen bookinghjemmeside for at maksimere synligheden.

**Kanalstrategi:**
- List på store platforme (Booking.com, Airbnb, VRBO)
- Brug regionale platforme til lokale markeder
- Byg din direkte bookinghjemmeside
- Brug en channel manager til realtidssynkronisering

## Strategi 4: Sæsonbetonede Kampagner

Skab målrettede kampagner for lavefterspørgselsperioder. Last-minute rabatter, tilbud på forlængede ophold og sæsonpakker kan udfylde huller i din kalender.

## Strategi 5: Fremragende Gæsteoplevelse

Glade gæster efterlader positive anmeldelser, som driver fremtidige bookinger. Fokuser på at overgå forventninger fra første forespørgsel til checkout.

**Tips til gæsteoplevelse:**
- Hurtige svartider (under 1 time)
- Personlige velkomstbeskeder
- Lokale anbefalinger og guider
- Smidig check-in proces
- Opfølgning efter checkout

## Måling af Succes

Følg din belægningsgrad månedligt og sammenlign år-over-år. Sigt efter konsekvent forbedring mens du opretholder din mål-ADR (Gennemsnitlig Dagspris).
    `,
  },
} as const;

// Helper type for blog article translation keys
export type BlogArticleTranslationKey = keyof typeof blogArticleTranslations.en;

// Slugs that have translations available
export const translatedArticleSlugs = [
  'go-to-button-quick-routes-accommodation',
  'flexible-pricing-options-now-available-in-i-rent',
  'new-ota-vacalia-available-in-i-rent',
  'guest-reviews-now-available-to-owners',
  'maximize-occupancy-rates-holiday-rentals',
  ...translatedArticleSlugsBatch2,
  ...translatedArticleSlugsBatch3,
  ...translatedArticleSlugsBatch4,
  ...translatedArticleSlugsBatch5,
  ...translatedArticleSlugsBatch6,
  ...translatedArticleSlugsBatch7,
] as const;

// Mapping from slug to translation key prefix
export const slugToTranslationKey: Record<string, string> = {
  'go-to-button-quick-routes-accommodation': 'blog.article.go-to-button',
  'flexible-pricing-options-now-available-in-i-rent': 'blog.article.flexible-pricing',
  'new-ota-vacalia-available-in-i-rent': 'blog.article.vacalia',
  'guest-reviews-now-available-to-owners': 'blog.article.owner-reviews',
  'maximize-occupancy-rates-holiday-rentals': 'blog.article.maximize-occupancy',
  ...slugToTranslationKeyBatch2,
  ...slugToTranslationKeyBatch3,
  ...slugToTranslationKeyBatch4,
  ...slugToTranslationKeyBatch5,
  ...slugToTranslationKeyBatch6,
  ...slugToTranslationKeyBatch7,
};

// Helper function to get translated blog content
export function getTranslatedBlogContent(
  slug: string,
  language: string,
  field: 'title' | 'excerpt' | 'content'
): string | null {
  const keyPrefix = slugToTranslationKey[slug];
  if (!keyPrefix) return null;

  const key = `${keyPrefix}.${field}`;
  
  // Check batch 1 translations first
  const langTranslations = blogArticleTranslations[language as keyof typeof blogArticleTranslations];
  if (langTranslations && (langTranslations as Record<string, string>)[key]) {
    return (langTranslations as Record<string, string>)[key];
  }
  
  // Check batch 2 translations
  const batch2Translations = blogArticleTranslationsBatch2[language as keyof typeof blogArticleTranslationsBatch2];
  if (batch2Translations && (batch2Translations as Record<string, string>)[key]) {
    return (batch2Translations as Record<string, string>)[key];
  }
  
  // Check batch 3 translations
  const batch3Translations = blogArticleTranslationsBatch3[language as keyof typeof blogArticleTranslationsBatch3];
  if (batch3Translations && (batch3Translations as Record<string, string>)[key]) {
    return (batch3Translations as Record<string, string>)[key];
  }
  
  // Check batch 4 translations
  const batch4Translations = blogArticleTranslationsBatch4[language as keyof typeof blogArticleTranslationsBatch4];
  if (batch4Translations && (batch4Translations as Record<string, string>)[key]) {
    return (batch4Translations as Record<string, string>)[key];
  }
  
  // Check batch 5 translations
  const batch5Translations = blogArticleTranslationsBatch5[language as keyof typeof blogArticleTranslationsBatch5];
  if (batch5Translations && (batch5Translations as Record<string, string>)[key]) {
    return (batch5Translations as Record<string, string>)[key];
  }
  
  // Check batch 6 translations
  const batch6Translations = blogArticleTranslationsBatch6[language as keyof typeof blogArticleTranslationsBatch6];
  if (batch6Translations && (batch6Translations as Record<string, string>)[key]) {
    return (batch6Translations as Record<string, string>)[key];
  }
  
  // Check batch 7 translations
  const batch7Translations = blogArticleTranslationsBatch7[language as keyof typeof blogArticleTranslationsBatch7];
  if (batch7Translations && (batch7Translations as Record<string, string>)[key]) {
    return (batch7Translations as Record<string, string>)[key];
  }
  
  // Fallback to English in batch 1
  if ((blogArticleTranslations.en as Record<string, string>)[key]) {
    return (blogArticleTranslations.en as Record<string, string>)[key];
  }
  
  // Fallback to English in batch 2
  if ((blogArticleTranslationsBatch2.en as Record<string, string>)[key]) {
    return (blogArticleTranslationsBatch2.en as Record<string, string>)[key];
  }
  
  // Fallback to English in batch 3
  if ((blogArticleTranslationsBatch3.en as Record<string, string>)[key]) {
    return (blogArticleTranslationsBatch3.en as Record<string, string>)[key];
  }
  
  // Fallback to English in batch 4
  if ((blogArticleTranslationsBatch4.en as Record<string, string>)[key]) {
    return (blogArticleTranslationsBatch4.en as Record<string, string>)[key];
  }
  
  // Fallback to English in batch 5
  if ((blogArticleTranslationsBatch5.en as Record<string, string>)[key]) {
    return (blogArticleTranslationsBatch5.en as Record<string, string>)[key];
  }
  
  // Fallback to English in batch 6
  if ((blogArticleTranslationsBatch6.en as Record<string, string>)[key]) {
    return (blogArticleTranslationsBatch6.en as Record<string, string>)[key];
  }
  
  // Fallback to English in batch 7
  if ((blogArticleTranslationsBatch7.en as Record<string, string>)[key]) {
    return (blogArticleTranslationsBatch7.en as Record<string, string>)[key];
  }
  
  return null;
}
